import { useEffect, useState } from 'react';
import harvestService from '../../services/harvest';
import { errorMessage } from '../../utils/apiResponse';
import { PACKOUT_GROUPS, type PackoutGroupKey, type PackoutReport } from '../../types/harvest';
import { inputClass } from '../fieldWork/FormModal';
import { DateRangeInputs, Notice } from '../labour/shared';
import { useDateRange } from '../labour/labourUtils';

/**
 * Packed-out kg, t/ha and grade % by cultivar area, block, cultivar, farm,
 * pick date or packout. Each bin carries its share of its packout's kg.
 */
export default function PackoutReportPanel() {
  const range = useDateRange();
  const [groupBy, setGroupBy] = useState<PackoutGroupKey>('cultivar_area');
  const [report, setReport] = useState<PackoutReport | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    harvestService.packoutReport(range.from, range.to, groupBy)
      .then(r => { if (live) { setReport(r); setError(null); } })
      .catch(e => { if (live) setError(errorMessage(e, 'Could not load the report')); });
    return () => { live = false; };
  }, [range.from, range.to, groupBy]);

  const gradeCols = report ? report.grades.filter(g => report.rows.some(r => r.grades.some(x => x.grade_id === g.id))) : [];
  const perHa = ['cultivar_area', 'block', 'farm'].includes(groupBy);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <DateRangeInputs {...range} />
        <label className="text-xs text-neutral-600">
          <span className="block mb-1">Group by</span>
          <select className={inputClass} value={groupBy} onChange={e => setGroupBy(e.target.value as PackoutGroupKey)}>
            {(Object.keys(PACKOUT_GROUPS) as PackoutGroupKey[]).map(k => <option key={k} value={k}>{PACKOUT_GROUPS[k].label}</option>)}
          </select>
        </label>
      </div>
      <p className="text-sm text-neutral-500">By packout date. Percentages are of kg tipped.</p>
      {error && <Notice tone="error">{error}</Notice>}
      {report && report.rows.length === 0 ? (
        <p className="text-sm text-neutral-500 py-6 text-center">No packouts in this range.</p>
      ) : report && (
        <>
          <p className="text-sm text-neutral-700">{report.totals.bins} bins · {report.totals.tonnes.toFixed(2)} t</p>
          <div className="overflow-x-auto border border-neutral-200 rounded-lg">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wide">
                <tr>
                  <th className="text-left px-3 py-2 font-medium">{PACKOUT_GROUPS[groupBy].label}</th>
                  <th className="text-right px-3 py-2 font-medium">Bins</th>
                  <th className="text-right px-3 py-2 font-medium">Tonnes</th>
                  {perHa && <th className="text-right px-3 py-2 font-medium">t/ha</th>}
                  {gradeCols.map(g => <th key={g.id} className="text-right px-3 py-2 font-medium whitespace-nowrap">{g.name}</th>)}
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100">
                {report.rows.map((r, i) => (
                  <tr key={i}>
                    <td className="px-3 py-2 whitespace-nowrap text-neutral-900">{(r[PACKOUT_GROUPS[groupBy].name] as string) ?? 'Block to confirm'}</td>
                    <td className="px-3 py-2 text-right">{r.bins}</td>
                    <td className="px-3 py-2 text-right">{r.tonnes.toFixed(2)}</td>
                    {perHa && <td className="px-3 py-2 text-right">{r.tonnes_per_ha ?? '—'}</td>}
                    {gradeCols.map(g => {
                      const x = r.grades.find(y => y.grade_id === g.id);
                      return <td key={g.id} className="px-3 py-2 text-right whitespace-nowrap">{x?.pct != null ? `${x.pct}%` : '—'}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
